import React, { useEffect, useState, useMemo, useRef } from "react";
import { RightOutlined, LockOutlined, UserOutlined, LogoutOutlined, InfoCircleOutlined, NumberOutlined } from "@ant-design/icons";
import {
  SidebarWrapper,
  SidebarHeader,
  ThemeLogo,
  MenuWrapper,
  MenuItem,
  MenuIcon,
  MenuLabel,
  MenuBadge,
  ToggleSwitchWrap,
  ToggleSwitch,
  CollapsedMenuItem,
  CollapsedMenuIcon,
  CollapsedBadge,
  BottomProfileWrapper,
  BottomProfileButton,
  BottomProfileInfo,
  BottomProfileName,
  ProfileDropdownMenu,
  ProfileDropdownItem,
  ProfileDropdownDivider,
  CollapsedBottomProfile,
  VersionWrapper,
  VersionDivider,
  VersionText,
} from "./SidebarStyle";
import { Tooltip, Popconfirm } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSidebarMenu, getUserData } from "../../utils/permissions";
import { useDispatch, useSelector } from "react-redux";
import { logoutApi } from "../../store/actions/loginAction";
import { PopconfirmGlobalStyles } from "../../pages/Surveys/Create/QuestionPreview.style";
import InitialsAvatar from "../InitialsAvatar/InitialsAvatar";
import ProfileEditModal from "../ProfileModals/ProfileEditModal";
import ChangePasswordModal from "../ProfileModals/ChangePasswordModal";

const Sidebar = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = getUserData();
  const userName = userData?.name || "User";
  const appVersionRaw = useSelector(state => state.setting?.getAppVersionState?.data?.value?.current);
  const appVersion = typeof appVersionRaw === 'object' ? appVersionRaw?.current : appVersionRaw;

  useEffect(() => {
    // Load collapsed state from localStorage when component mounts
    const savedCollapseState = localStorage.getItem("sidebar_collapsed");
    if (savedCollapseState !== null) {
      props.toggle(savedCollapseState === "true");
    }
  }, []);

  // Also update localStorage when collapsed state changes from parent
  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", props.collapsed.toString());
  }, [props.collapsed]);

  // Function to get the current selected key based on the route
  const getCurrentSelectedKey = () => {
    const routeMap = {
      "/": "dashboard",
      "/projects/": "projects",
      "/insight-engine/": "insights",
      "/surveys/": "survey",
      "/polling-agents/": "aijdv",
      "/campaigns/": "campaigns",
      "/insight-of-day/": "insightday",
      "/manage": "manage",
      "/organizations": "organizations",
    };

    // Check for exact match first
    if (routeMap[location.pathname]) {
      return routeMap[location.pathname];
    }

    // Check for partial matches (e.g., /projects/123 should match /projects/)
    for (const [route, key] of Object.entries(routeMap)) {
      if (location.pathname.startsWith(route) && route !== "/") {
        return key;
      }
    }

    // Default to Dashboard if no match found
    return "dashboard";
  };

  const handleMenuClick = (route) => {
    navigate(route);
  };

  const handleLogout = () => {
    dispatch(logoutApi());
    navigate("/login");
  };

  const currentSelected = getCurrentSelectedKey();

  // Get menu items from centralized permission system
  // This reads from ROUTES config in permissions.js and filters based on user permissions
  const menuItems = useMemo(() => {
    const sidebarRoutes = getSidebarMenu();
    // Transform ROUTES format to menu item format
    return sidebarRoutes.map((route) => ({
      key: route.key,
      label: route.label,
      description: route.description,
      icon: route.icon,
      route: route.path,
      badge: route.badge,
    }));
  }, []);

  // Expanded content only when sidebar is open (no expand on hover — open only via toggle click)
  const shouldShowExpanded = !props.collapsed;

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    setIsProfileEditModalOpen(true);
  };

  const handleChangePasswordClick = () => {
    setIsProfileMenuOpen(false);
    setIsChangePasswordModalOpen(true);
  };

  return (
    <SidebarWrapper
      collapsed={props.collapsed}
      isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PopconfirmGlobalStyles />
      {/* Header: when open = full logo + toggle; when closed = small logo, toggle overlays logo on hover */}
      <SidebarHeader collapsed={props.collapsed}>
        <ThemeLogo collapsed={props.collapsed}>
          <Link to="/">
            {props.collapsed ? (
              <img src="/favicon.ico" alt="CrowdVox" />
            ) : (
              <img src="/images/logo.svg" alt="CrowdVox" />
            )}
          </Link>
        </ThemeLogo>
        <ToggleSwitchWrap collapsed={props.collapsed} isHovered={isHovered}>
          <Tooltip title={props.collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <ToggleSwitch collapsed={props.collapsed}>
              <img
                src="/images/switch-sidebar.svg"
                alt={props.collapsed ? "Expand sidebar" : "Collapse sidebar"}
                onClick={(e) => {
                  e.stopPropagation();
                  props.toggle(!props.collapsed);
                }}
              />
            </ToggleSwitch>
          </Tooltip>
        </ToggleSwitchWrap>
      </SidebarHeader>


      {/* Navigation Menu */}
      <MenuWrapper>
        {shouldShowExpanded
          ? // Expanded view - show full menu items (either permanently expanded or hover-expanded)
            menuItems.map((item) => (
              <MenuItem
                key={item.key}
                active={currentSelected === item.key}
                onClick={() => handleMenuClick(item.route)}
              >
                <MenuIcon active={currentSelected === item.key}>
                  <img src={item.icon} alt={item.label} />
                </MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
                {item.description && (
                  <Tooltip title={item.description} placement="right">
                    <InfoCircleOutlined
                      style={{
                        fontSize: 12,
                        color: '#9CA3AF',
                        marginLeft: 'auto',
                        opacity: 0.7,
                        cursor: 'help'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                )}
                {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
              </MenuItem>
            ))
          : // Collapsed view - show only icons with tooltips
            menuItems.map((item) => (
              <Tooltip key={item.key} title={item.label} placement="right">
                <CollapsedMenuItem
                  active={currentSelected === item.key}
                  onClick={() => handleMenuClick(item.route)}
                >
                  <CollapsedMenuIcon active={currentSelected === item.key}>
                    <img src={item.icon} alt={item.label} />
                  </CollapsedMenuIcon>
                  {item.badge && <CollapsedBadge>{item.badge}</CollapsedBadge>}
                </CollapsedMenuItem>
              </Tooltip>
            ))}
      </MenuWrapper>

      {/* ChatGPT-style Bottom Profile Menu */}
      {shouldShowExpanded ? (
        <BottomProfileWrapper ref={profileMenuRef}>
          {/* Dropdown Menu */}
          <ProfileDropdownMenu isOpen={isProfileMenuOpen}>
            <ProfileDropdownItem onClick={handleProfileClick}>
              <UserOutlined />
              Edit Profile
            </ProfileDropdownItem>
            <ProfileDropdownItem onClick={handleChangePasswordClick}>
              <LockOutlined />
              Change Password
            </ProfileDropdownItem>
            <ProfileDropdownDivider />
            <Popconfirm
              title="Confirm Logout"
              description="Are you sure you want to logout?"
              okText="Yes"
              cancelText="No"
              onConfirm={handleLogout}
              placement="right"
            >
              <ProfileDropdownItem className="danger">
                <LogoutOutlined />
                Logout
              </ProfileDropdownItem>
            </Popconfirm>
          </ProfileDropdownMenu>

          {/* Profile Button */}
          <BottomProfileButton isOpen={isProfileMenuOpen} onClick={toggleProfileMenu}>
            <InitialsAvatar name={userName} size={36} />
            <BottomProfileInfo>
              <BottomProfileName>{userName}</BottomProfileName>
            </BottomProfileInfo>
            <RightOutlined style={{ color: "#6F7B8C", fontSize: "12px", transform: isProfileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          </BottomProfileButton>
        </BottomProfileWrapper>
      ) : (
        // Collapsed view - show avatar with tooltip and dropdown (always visible when closed, not hidden on hover)
        props.collapsed && (
          <BottomProfileWrapper ref={profileMenuRef}>
            <ProfileDropdownMenu isOpen={isProfileMenuOpen}>
              <ProfileDropdownItem onClick={handleProfileClick}>
                <UserOutlined />
                Edit Profile
              </ProfileDropdownItem>
              <ProfileDropdownItem onClick={handleChangePasswordClick}>
                <LockOutlined />
                Change Password
              </ProfileDropdownItem>
              <ProfileDropdownDivider />
              <Popconfirm
                title="Confirm Logout"
                description="Are you sure you want to logout?"
                okText="Yes"
                cancelText="No"
                onConfirm={handleLogout}
                placement="right"
              >
                <ProfileDropdownItem className="danger">
                  <LogoutOutlined />
                  Logout
                </ProfileDropdownItem>
              </Popconfirm>
            </ProfileDropdownMenu>
            <Tooltip title={userName} placement="right">
              <CollapsedBottomProfile onClick={toggleProfileMenu}>
                <InitialsAvatar name={userName} size={28} />
              </CollapsedBottomProfile>
            </Tooltip>
          </BottomProfileWrapper>
        )
      )}

      {/* Version Number - below profile */}
      {appVersion && shouldShowExpanded && (
        <VersionWrapper>
          <VersionDivider />
          <VersionText>Version {appVersion}</VersionText>
        </VersionWrapper>
      )}
      {appVersion && props.collapsed && (
        <Tooltip title={`Version ${appVersion}`} placement="right">
          <VersionWrapper style={{ textAlign: 'center', padding: '4px 0' }}>
            <NumberOutlined style={{ fontSize: 12, color: '#9CA3AF', opacity: 0.5 }} />
          </VersionWrapper>
        </Tooltip>
      )}

      {/* Profile Edit Modal */}
      <ProfileEditModal
        open={isProfileEditModalOpen}
        onClose={() => setIsProfileEditModalOpen(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </SidebarWrapper>
  );
};

export default Sidebar;
